import React, {
  Children,
  cloneElement,
  ComponentPropsWithoutRef,
  CSSProperties,
  isValidElement,
  ReactElement,
  ReactNode,
  useContext,
} from "react";
import clsx from "clsx";
import { mergeProps } from "@react-aria/utils";
import { DialogContext } from "./dialog-context";
import { InputSize } from "../../forms/input-field/input-size";
import { DismissButton } from "./dismiss-button";
import {
  isRenderedAsAndroid,
  isRenderedAsIOS,
  isRenderedAsMobileApp,
} from "@common/core/platform-detection";

export type DialogSize =
  | InputSize
  | "2xl"
  | "auto"
  | "fullscreen"
  | "fullscreenTakeover"
  | string;

export interface DialogProps extends Omit<
  ComponentPropsWithoutRef<"div">,
  "size"
> {
  children: ReactNode;
  size?: DialogSize;
  background?: string;
  className?: string;
  radius?: string;
  maxWidth?: string;
  maxHeight?: string;
  shadow?: string;
}

export function Dialog(props: DialogProps) {
  const {
    type = "modal",
    dialogProps,
    ...contextProps
  } = useContext(DialogContext);

  const {
    children,
    className,
    size = "md",
    background,
    radius = "rounded-panel",
    maxWidth = "max-w-dialog",
    maxHeight = "max-h-dialog",
    shadow = "shadow-2xl",
    ...domProps
  } = props;
  const isFullscreenTakeover = size === "fullscreenTakeover";
  const shouldUseIosPopoverSurface =
    isRenderedAsMobileApp() &&
    isRenderedAsIOS() &&
    !isFullscreenTakeover &&
    type === "popover";
  const shouldUseIosTraySurface =
    isRenderedAsMobileApp() && isRenderedAsIOS() && type === "tray";
  const shouldUseAmbientModalSurface =
    isRenderedAsMobileApp() &&
    type === "modal" &&
    (isRenderedAsAndroid() || isRenderedAsIOS());
  const shouldUseAndroidPopoverAmbientSurface =
    isRenderedAsMobileApp() &&
    isRenderedAsAndroid() &&
    type === "popover";
  const shouldUseNativeOverlaySurface =
    shouldUseIosPopoverSurface ||
    shouldUseIosTraySurface ||
    shouldUseAmbientModalSurface ||
    shouldUseAndroidPopoverAmbientSurface;
  // If rendered in a popover or tray there won't be a visible dismiss button,
  // so we render a hidden one for screen readers.
  let dismissButton: ReactElement | null = null;
  if (type === "popover" || type === "tray") {
    dismissButton = <DismissButton onDismiss={contextProps.close} />;
  }

  const isTrayOrFullScreen =
    isFullscreenTakeover || type === "tray" || type === "stacked";
  const mergedClassName = clsx(
    "mx-auto pointer-events-auto outline-none flex flex-col overflow-hidden",
    shouldUseIosPopoverSurface
      ? "relative ios-floating-surface rounded-panel bg-transparent border-transparent shadow-none"
      : shouldUseIosTraySurface
        ? "ios-tray-surface"
      : shouldUseAmbientModalSurface || shouldUseAndroidPopoverAmbientSurface
        ? "relative player-ambient-scene app-ambient-floating-surface rounded-panel bg-transparent border-transparent shadow-none"
        : background || "bg",
    shouldUseNativeOverlaySurface && "native-overlay-motion-surface",
    type !== "tray" && type !== "stacked" && sizeStyle(size),
    type === "tray" && "tray-dialog-surface",
    type === "stacked" && "w-full h-full",
    !isFullscreenTakeover && `${maxHeight}`,
    !isFullscreenTakeover &&
      type === "popover" &&
      !shouldUseNativeOverlaySurface &&
      `${shadow}`,
    !isFullscreenTakeover &&
      type !== "stacked" &&
      !shouldUseNativeOverlaySurface &&
      `border`,
    !isTrayOrFullScreen && `${radius} ${maxWidth}`,
    className,
  );

  return (
    <div
      {...mergeProps({ role: "dialog", tabIndex: -1 }, dialogProps, domProps)}
      style={{ ...props.style, "--be-dialog-padding": "24px" } as CSSProperties}
      aria-modal
      className={mergedClassName}
    >
      <div className="flex min-h-0 flex-auto flex-col">
        {Children.toArray(children).map((child) => {
          if (isValidElement<DialogProps>(child)) {
            return cloneElement<DialogProps>(child, {
              size: child.props.size ?? size,
            });
          }
          return child;
        })}
        {dismissButton}
      </div>
    </div>
  );
}

function sizeStyle(dialogSize?: DialogSize) {
  switch (dialogSize) {
    case "2xs":
      return "w-256";
    case "xs":
      return "w-320";
    case "sm":
      return "w-384";
    case "md":
      return "w-440";
    case "md2":
      return "w-[500px]";
    case "lg":
      return "w-620";
    case "xl":
      return "w-780";
    case "2xl":
      return "w-850";
    case "fullscreen":
      return "w-1280";
    case "fullscreenTakeover":
      return "w-full h-full";
    default:
      return dialogSize;
  }
}
